import { SupabaseJob, Job } from '@applicator/shared'

import { DateMode } from '../JobDateChips/types'

import { mapSupabaseToJob } from './helpers'

// Helper function to merge updated dates into supabase job
export function mergeUpdatedDatesIntoSupabaseJob(
  sbJob: SupabaseJob,
  updatedDates: Record<string, unknown>
): SupabaseJob {
  return {
    ...sbJob,
    ...(updatedDates.saved_at !== undefined && { saved_at: updatedDates.saved_at as string | null }),
    ...(updatedDates.deadline_at !== undefined && { deadline_at: updatedDates.deadline_at as string | null }),
    ...(updatedDates.applied_at !== undefined && { applied_at: updatedDates.applied_at as string | null }),
    ...(updatedDates.follow_up_at !== undefined && { follow_up_at: updatedDates.follow_up_at as string | null }),
    ...(updatedDates.date_saved_mode !== undefined && { date_saved_mode: updatedDates.date_saved_mode as string }),
    ...(updatedDates.deadline_mode !== undefined && { deadline_mode: updatedDates.deadline_mode as string }),
    ...(updatedDates.date_applied_mode !== undefined && { date_applied_mode: updatedDates.date_applied_mode as string }),
    ...(updatedDates.follow_up_mode !== undefined && { follow_up_mode: updatedDates.follow_up_mode as string }),
    ...(updatedDates.date_saved_manual !== undefined && { date_saved_manual: updatedDates.date_saved_manual as string | null }),
    ...(updatedDates.deadline_manual !== undefined && { deadline_manual: updatedDates.deadline_manual as string | null }),
    ...(updatedDates.date_applied_manual !== undefined && { date_applied_manual: updatedDates.date_applied_manual as string | null }),
    ...(updatedDates.follow_up_manual !== undefined && { follow_up_manual: updatedDates.follow_up_manual as string | null }),
  }
}

// Helper function to update supabaseJobs state with updated dates
export function updateSupabaseJobsWithDates(
  jobId: string,
  updatedDates: Record<string, unknown>,
  supabaseJobs: SupabaseJob[],
  setSupabaseJobs: React.Dispatch<React.SetStateAction<SupabaseJob[]>>
) {
  setSupabaseJobs((prevSupabaseJobs) =>
    prevSupabaseJobs.map(sbJob =>
      sbJob.id === jobId ? mergeUpdatedDatesIntoSupabaseJob(sbJob, updatedDates) : sbJob
    )
  )
}

// Helper function to update single job with dates
export function updateSingleJobWithDates(
  job: Job,
  jobId: string,
  updatedDates: Record<string, unknown>,
  supabaseJobs: SupabaseJob[]
): Job | null {
  if (job.id !== jobId) {
    return null
  }
  const currentSupabaseJob = supabaseJobs.find(sb => sb.id === jobId)
  if (!currentSupabaseJob) {
    return null
  }
  const updatedSupabaseJob = mergeUpdatedDatesIntoSupabaseJob(currentSupabaseJob, updatedDates)
  return mapSupabaseToJob(updatedSupabaseJob)
}

// Helper function to update jobs state with updated dates
export function updateJobsStateWithDates(
  jobId: string,
  updatedDates: Record<string, unknown>,
  jobs: Job[],
  supabaseJobs: SupabaseJob[],
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>
) {
  setJobs((prevJobs) => {
    return prevJobs.map(job => {
      const updatedJob = updateSingleJobWithDates(job, jobId, updatedDates, supabaseJobs)
      return updatedJob || job
    })
  })
}

// Helper function to prepare initial job dates for JobDateChips
export function prepareInitialJobDates(supabaseJob: SupabaseJob) {
  return {
    saved_at: supabaseJob.saved_at || null,
    deadline_at: supabaseJob.deadline_at || null,
    applied_at: supabaseJob.applied_at || null,
    follow_up_at: supabaseJob.follow_up_at || null,
    date_saved_mode: (supabaseJob.date_saved_mode as DateMode | undefined) || 'auto',
    deadline_mode: (supabaseJob.deadline_mode as DateMode | undefined) || 'auto',
    date_applied_mode: (supabaseJob.date_applied_mode as DateMode | undefined) || 'auto',
    follow_up_mode: (supabaseJob.follow_up_mode as DateMode | undefined) || 'auto',
    date_saved_manual: supabaseJob.date_saved_manual || null,
    deadline_manual: supabaseJob.deadline_manual || null,
    date_applied_manual: supabaseJob.date_applied_manual || null,
    follow_up_manual: supabaseJob.follow_up_manual || null,
  }
}
